import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { DischargeFormData } from '../../../../types/formData'
import { DischargeFormField } from '../../../../types/formField'
import { DischargeAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Neurological subsection adapter
 * Handles neurological assessment including PHQ2 scoring and seizure management (15 fields)
 */
export class NeurologicalAdapter extends BaseSubsectionAdapter<DischargeFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<DischargeFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Headache assessment
    const headaches = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_HEADACHES
    )?.at(0)

    const headacheType = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_HEADACHE_TYPE
    )

    // Seizure assessment
    const seizures = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURES
    )?.at(0)

    const seizureDate = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_DATE
    )?.at(0)

    const seizureType = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_TYPE
    )

    const seizurePrecautions = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_PRECAUTIONS
    )

    // General neurological status
    const neuroStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_NEURO_STATUS
    )

    const alteredAffect = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_ALTERED_AFFECT
    )?.at(0)

    const alteredAffectFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_ALTERED_AFFECT_FACTORS
    )

    // PHQ2 Depression Screening (Patient Health Questionnaire-2)
    const phq2Screening = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_PHQ2_SCREENING
    )?.at(0)

    const phq2Notes = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_PHQ2_NOTES
    )?.at(0)

    // PHQ2 individual questions (scored 0-3 each)
    const interest = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_INTEREST
    )?.at(0)

    const depressed = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_DEPRESSED
    )?.at(0)

    // Caregiver assessment
    const caregiverDifficulty = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_CAREGIVER_DIFFICULTY
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [DischargeFormField.NEURO_HEADACHES]: headaches,
      [DischargeFormField.NEURO_HEADACHE_TYPE]: headacheType,
      [DischargeFormField.NEURO_SEIZURES]: seizures,
      [DischargeFormField.NEURO_SEIZURE_DATE]: seizureDate,
      [DischargeFormField.NEURO_SEIZURE_TYPE]: seizureType,
      [DischargeFormField.NEURO_SEIZURE_PRECAUTIONS]: seizurePrecautions,
      [DischargeFormField.NEURO_STATUS]: neuroStatus,
      [DischargeFormField.NEURO_ALTERED_AFFECT]: alteredAffect,
      [DischargeFormField.NEURO_ALTERED_AFFECT_FACTORS]: alteredAffectFactors,
      [DischargeFormField.NEURO_PHQ2_SCREENING]: phq2Screening,
      [DischargeFormField.NEURO_PHQ2_NOTES]: phq2Notes,
      [DischargeFormField.NEURO_INTEREST]: interest,
      [DischargeFormField.NEURO_DEPRESSED]: depressed,
      [DischargeFormField.NEURO_CAREGIVER_DIFFICULTY]: caregiverDifficulty,
      [DischargeFormField.NEURO_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(DischargeFormField.NEURO_HEADACHES)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_HEADACHES,
        value: this.getFormDataField(DischargeFormField.NEURO_HEADACHES)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.NEURO_HEADACHE_TYPE)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_HEADACHE_TYPE,
        value: this.getFormDataField(DischargeFormField.NEURO_HEADACHE_TYPE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.NEURO_SEIZURES)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURES,
        value: this.getFormDataField(DischargeFormField.NEURO_SEIZURES)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.NEURO_SEIZURE_DATE)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_DATE,
        value: this.getFormDataField(DischargeFormField.NEURO_SEIZURE_DATE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.NEURO_SEIZURE_TYPE)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_TYPE,
        value: this.getFormDataField(DischargeFormField.NEURO_SEIZURE_TYPE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.NEURO_SEIZURE_PRECAUTIONS)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_PRECAUTIONS,
        value: this.getFormDataField(DischargeFormField.NEURO_SEIZURE_PRECAUTIONS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.NEURO_STATUS)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_NEURO_STATUS,
        value: this.getFormDataField(DischargeFormField.NEURO_STATUS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.NEURO_ALTERED_AFFECT)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_ALTERED_AFFECT,
        value: this.getFormDataField(DischargeFormField.NEURO_ALTERED_AFFECT)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.NEURO_ALTERED_AFFECT_FACTORS)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_ALTERED_AFFECT_FACTORS,
        value: this.getFormDataField(DischargeFormField.NEURO_ALTERED_AFFECT_FACTORS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.NEURO_PHQ2_SCREENING)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_PHQ2_SCREENING,
        value: this.getFormDataField(DischargeFormField.NEURO_PHQ2_SCREENING)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.NEURO_PHQ2_NOTES)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_PHQ2_NOTES,
        value: this.getFormDataField(DischargeFormField.NEURO_PHQ2_NOTES)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.NEURO_INTEREST)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_INTEREST,
        value: this.getFormDataField(DischargeFormField.NEURO_INTEREST)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.NEURO_DEPRESSED)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_DEPRESSED,
        value: this.getFormDataField(DischargeFormField.NEURO_DEPRESSED)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.NEURO_CAREGIVER_DIFFICULTY)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_CAREGIVER_DIFFICULTY,
        value: this.getFormDataField(DischargeFormField.NEURO_CAREGIVER_DIFFICULTY)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.NEURO_SYSTEM_SUMMARY)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SYSTEM_SUMMARY,
        value: this.getFormDataField(DischargeFormField.NEURO_SYSTEM_SUMMARY)
      })
    }

    return mutations
  }
}
